import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, X, Sparkles } from "lucide-react";

export default function SuccessModal({ isOpen, onClose, userName }) {
    if (!isOpen) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    >
                        {/* Modal */}
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0, y: 50 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.8, opacity: 0, y: 50 }}
                            transition={{ type: "spring", duration: 0.5 }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative max-w-md w-full bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 shadow-2xl border border-cyan-500/30"
                        >
                            {/* Bouton fermeture */}
                            <motion.button
                                whileHover={{ scale: 1.1, rotate: 90 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={onClose}
                                className="absolute top-4 right-4 p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
                            >
                                <X className="w-5 h-5 text-gray-400" />
                            </motion.button>

                            {/* Icône succès animée */}
                            <div className="flex justify-center mb-6">
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1, rotate: 360 }}
                                    transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                                    className="relative"
                                >
                                    <motion.div
                                        animate={{
                                            scale: [1, 1.2, 1],
                                            opacity: [0.5, 0.8, 0.5]
                                        }}
                                        transition={{ duration: 2, repeat: Infinity }}
                                        className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full blur-xl"
                                    />
                                    <div className="relative w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center shadow-2xl">
                                        <CheckCircle className="w-12 h-12 text-white" strokeWidth={3} />
                                    </div>
                                </motion.div>
                            </div>

                            {/* Confetti effect */}
                            <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl">
                                {[...Array(12)].map((_, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ y: -20, x: Math.random() * 100 + "%", opacity: 1 }}
                                        animate={{
                                            y: [null, "120%"],
                                            rotate: [0, 360],
                                            opacity: [1, 0]
                                        }}
                                        transition={{
                                            duration: 2 + Math.random() * 1,
                                            delay: Math.random() * 0.5,
                                            ease: "easeOut"
                                        }}
                                        className="absolute w-2 h-2 rounded-full"
                                        style={{
                                            background: `hsl(${Math.random() * 360}, 70%, 60%)`,
                                            left: Math.random() * 100 + "%"
                                        }}
                                    />
                                ))}
                            </div>

                            {/* Titre */}
                            <motion.h2
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                                className="text-3xl font-bold text-white text-center mb-3"
                            >
                                Message envoyé ! 🎉
                            </motion.h2>

                            {/* Message personnalisé */}
                            <motion.p
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.4 }}
                                className="text-gray-300 text-center mb-2"
                            >
                                Merci <span className="text-cyan-400 font-semibold">{userName}</span> !
                            </motion.p>

                            <motion.p
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.5 }}
                                className="text-gray-400 text-center mb-8"
                            >
                                Votre message a bien été reçu. Notre équipe vous répondra dans les plus brefs délais.
                            </motion.p>

                            {/* Détails supplémentaires */}
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.6 }}
                                className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl p-4 mb-6"
                            >
                                <div className="flex items-start gap-3">
                                    <Sparkles className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-sm text-gray-300 font-medium mb-1">
                                            Délai de réponse estimé
                                        </p>
                                        <p className="text-xs text-gray-400">
                                            Nous nous engageons à vous répondre sous 24 heures ouvrées.
                                        </p>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Bouton CTA */}
                            <motion.button
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.7 }}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={onClose}
                                className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-cyan-500/50 transition-all duration-300"
                            >
                                Parfait, merci !
                            </motion.button>
                        </motion.div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
